import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CytologyFormSchema, FormSection, FormField, CheckboxGroupField, DEFAULT_CYTOLOGY_SCHEMA } from '../../types/formSchema'
import FieldLayoutEditor from '@/components/forms/FieldLayoutEditor'
import { logger } from '@/utils/logger'

interface FieldEditorModal {
  isOpen: boolean
  mode: 'add' | 'edit'
  sectionId: string
  field?: FormField | CheckboxGroupField
}

const CytologyFormBuilder: React.FC = () => {
  const navigate = useNavigate()
  const [schema, setSchema] = useState<CytologyFormSchema>(DEFAULT_CYTOLOGY_SCHEMA)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [expandedSection, setExpandedSection] = useState<string | null>(null)

  // Field editor modal state
  const [fieldEditor, setFieldEditor] = useState<FieldEditorModal>({
    isOpen: false,
    mode: 'add',
    sectionId: ''
  })
  const [newField, setNewField] = useState<Partial<FormField | CheckboxGroupField>>({
    id: '',
    label: '',
    type: 'text',
    required: false,
    enabled: true,
    order: 1
  })

  useEffect(() => {
    fetchSchema()
  }, [])

  const fetchSchema = async () => {
    try {
      const response = await fetch('/api/v1/pathology/cytology-templates/default/')
      if (response.ok) {
        const template = await response.json()
        logger.debug('📥 Loaded template from database:', template)
        if (template.config && template.config.formSchema) {
          logger.debug('✅ Using formSchema from database')
          setSchema(template.config.formSchema)
        } else {
          logger.debug('⚠️ No formSchema in config, using default')
          setSchema(DEFAULT_CYTOLOGY_SCHEMA)
        }
      } else if (response.status === 404) {
        logger.debug('ℹ️ No default template found (404), using default schema')
        logger.debug('💡 Will create new template on first save')
        setSchema(DEFAULT_CYTOLOGY_SCHEMA)
      } else {
        console.warn('⚠️ Error loading template:', response.status)
        setSchema(DEFAULT_CYTOLOGY_SCHEMA)
      }
    } catch (error) {
      console.error('❌ Error fetching schema:', error)
      setSchema(DEFAULT_CYTOLOGY_SCHEMA)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Get existing template
      const response = await fetch('/api/v1/pathology/cytology-templates/default/')
      let templateId = null
      let existingConfig = {}

      if (response.ok) {
        const existingTemplate = await response.json()
        templateId = existingTemplate.id
        existingConfig = existingTemplate.config || {}
        logger.debug('📋 Existing template:', existingTemplate)
      }

      const payloadToSave = {
        name: 'Default Cytology Template',
        description: 'System default cytology report template with custom form schema',
        config: {
          ...existingConfig,
          formSchema: schema
        },
        is_active: true,
        is_default: true
      }

      logger.debug('💾 Saving schema:', payloadToSave)
      logger.debug('📊 Schema sections count:', schema.sections.length)
      logger.debug('📝 Total fields:', schema.sections.reduce((sum, s) => sum + s.fields.length, 0))

      // Update or create template with new schema
      const saveResponse = await fetch(
        templateId
          ? `/api/v1/pathology/cytology-templates/${templateId}/`
          : '/api/v1/pathology/cytology-templates/',
        {
          method: templateId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadToSave)
        }
      )

      if (saveResponse.ok) {
        const savedData = await saveResponse.json()
        logger.debug('✅ Saved successfully:', savedData)
        toast.success('Đã lưu cấu hình form thành công!')

        // Verify by fetching again
        setTimeout(async () => {
          const verifyResponse = await fetch('/api/v1/pathology/cytology-templates/default/')
          if (verifyResponse.ok) {
            const verifiedTemplate = await verifyResponse.json()
            logger.debug('🔍 Verification - template after save:', verifiedTemplate)
            if (verifiedTemplate.config && verifiedTemplate.config.formSchema) {
              logger.debug('✅ FormSchema verified in database')
            } else {
              console.warn('⚠️ FormSchema NOT found in saved template!')
            }
          }
        }, 1000)
      } else {
        const error = await saveResponse.json()
        console.error('❌ Save error:', error)
        toast.error('Lỗi khi lưu: ' + JSON.stringify(error))
      }
    } catch (error) {
      console.error('❌ Error saving schema:', error)
      toast.error('Lỗi khi lưu cấu hình form')
    } finally {
      setSaving(false)
    }
  }

  const toggleSection = (sectionId: string) => {
    setSchema(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, enabled: !section.enabled }
          : section
      )
    }))
  }

  const toggleField = (sectionId: string, fieldId: string) => {
    setSchema(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map(field =>
                field.id === fieldId
                  ? { ...field, enabled: !field.enabled }
                  : field
              )
            }
          : section
      )
    }))
  }

  const toggleCheckboxOption = (sectionId: string, fieldId: string, optionId: string) => {
    setSchema(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map(field => {
                if (field.id === fieldId && field.type === 'checkboxGroup') {
                  const checkboxField = field as CheckboxGroupField
                  return {
                    ...checkboxField,
                    options: checkboxField.options.map(opt =>
                      opt.id === optionId
                        ? { ...opt, enabled: !opt.enabled }
                        : opt
                    )
                  }
                }
                return field
              })
            }
          : section
      )
    }))
  }

  const openAddFieldModal = (sectionId: string) => {
    const section = schema.sections.find(s => s.id === sectionId)
    const maxOrder = section ? Math.max(...section.fields.map(f => f.order), 0) : 0

    setNewField({
      id: '',
      label: '',
      type: 'text',
      required: false,
      enabled: true,
      order: maxOrder + 1
    })
    setFieldEditor({
      isOpen: true,
      mode: 'add',
      sectionId
    })
  }

  const openEditFieldModal = (sectionId: string, field: FormField | CheckboxGroupField) => {
    setNewField(field)
    setFieldEditor({
      isOpen: true,
      mode: 'edit',
      sectionId,
      field
    })
  }

  const closeFieldModal = () => {
    setFieldEditor({
      isOpen: false,
      mode: 'add',
      sectionId: ''
    })
  }

  const saveField = () => {
    if (!newField.id || !newField.label) {
      toast.error('Vui lòng nhập ID và Label cho field')
      return
    }

    const fieldToSave: FormField = {
      id: newField.id,
      label: newField.label,
      type: newField.type || 'text',
      required: newField.required || false,
      enabled: newField.enabled !== false,
      order: newField.order || 1,
      placeholder: newField.placeholder,
      options: newField.options as any, // Allow both CheckboxOption[] and {value, label}[]
      defaultValue: newField.defaultValue
    }

    if (fieldEditor.mode === 'add') {
      // Add new field
      setSchema(prev => ({
        ...prev,
        sections: prev.sections.map(section =>
          section.id === fieldEditor.sectionId
            ? {
                ...section,
                fields: [...section.fields, fieldToSave].sort((a, b) => a.order - b.order)
              }
            : section
        )
      }))
      toast.success('Đã thêm field mới')
    } else {
      // Edit existing field
      setSchema(prev => ({
        ...prev,
        sections: prev.sections.map(section =>
          section.id === fieldEditor.sectionId
            ? {
                ...section,
                fields: section.fields.map(f =>
                  f.id === fieldEditor.field?.id ? fieldToSave : f
                )
              }
            : section
        )
      }))
      toast.success('Đã cập nhật field')
    }

    closeFieldModal()
  }

  const deleteField = (sectionId: string, fieldId: string) => {
    if (!window.confirm('Bạn có chắc muốn xóa field này?')) return

    setSchema(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.filter(f => f.id !== fieldId)
            }
          : section
      )
    }))
    toast.success('Đã xóa field')
  }

  const moveFieldUp = (sectionId: string, fieldId: string) => {
    setSchema(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id !== sectionId) return section

        const fields = [...section.fields]
        const index = fields.findIndex(f => f.id === fieldId)
        if (index > 0) {
          [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]]
          // Update order
          fields.forEach((f, i) => { f.order = i + 1 })
        }
        return { ...section, fields }
      })
    }))
  }

  const moveFieldDown = (sectionId: string, fieldId: string) => {
    setSchema(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id !== sectionId) return section

        const fields = [...section.fields]
        const index = fields.findIndex(f => f.id === fieldId)
        if (index < fields.length - 1) {
          [fields[index], fields[index + 1]] = [fields[index + 1], fields[index]]
          // Update order
          fields.forEach((f, i) => { f.order = i + 1 })
        }
        return { ...section, fields }
      })
    }))
  }

  const moveSectionUp = (sectionId: string) => {
    setSchema(prev => {
      const sections = [...prev.sections]
      const index = sections.findIndex(s => s.id === sectionId)
      if (index > 0) {
        [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]]
        // Update order
        sections.forEach((s, i) => { s.order = i + 1 })
      }
      return { ...prev, sections }
    })
  }

  const moveSectionDown = (sectionId: string) => {
    setSchema(prev => {
      const sections = [...prev.sections]
      const index = sections.findIndex(s => s.id === sectionId)
      if (index < sections.length - 1) {
        [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]]
        // Update order
        sections.forEach((s, i) => { s.order = i + 1 })
      }
      return { ...prev, sections }
    })
  }

  const renderFieldToggle = (section: FormSection, field: FormField | CheckboxGroupField) => {
    if (field.type === 'checkboxGroup') {
      const checkboxField = field as CheckboxGroupField
      return (
        <div key={field.id} className="ml-6 mb-2">
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={field.enabled}
                onChange={() => toggleField(section.id, field.id)}
                className="w-4 h-4"
              />
              <span className="font-medium text-gray-700">{field.label}</span>
            </label>
          </div>
          {field.enabled && (
            <div className="ml-6 space-y-1">
              {checkboxField.options.map(option => (
                <label key={option.id} className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    checked={option.enabled}
                    onChange={() => toggleCheckboxOption(section.id, field.id, option.id)}
                    className="w-3 h-3"
                  />
                  <span className={option.enabled ? 'text-gray-700' : 'text-gray-400 line-through'}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      )
    }

    return (
      <div key={field.id} className="ml-6 mb-2 flex items-center justify-between group">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={field.enabled}
            onChange={() => toggleField(section.id, field.id)}
            className="w-4 h-4"
          />
          <span className={field.enabled ? 'text-gray-700' : 'text-gray-400 line-through'}>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
            <span className="text-xs text-gray-400 ml-2">({field.type})</span>
          </span>
        </label>
        <div className="opacity-0 group-hover:opacity-100 flex space-x-1">
          <button
            onClick={() => moveFieldUp(section.id, field.id)}
            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            title="Di chuyển lên"
          >
            ▲
          </button>
          <button
            onClick={() => moveFieldDown(section.id, field.id)}
            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            title="Di chuyển xuống"
          >
            ▼
          </button>
          <button
            onClick={() => openEditFieldModal(section.id, field)}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            title="Chỉnh sửa"
          >
            ✏️
          </button>
          <button
            onClick={() => deleteField(section.id, field.id)}
            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
            title="Xóa"
          >
            🗑️
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return <div className="p-8 text-center">Đang tải...</div>
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">🔧 Quản lý Form Template</h1>
              <p className="text-gray-600 mt-2">Tùy chỉnh các trường thông tin trong phiếu kết quả cytology</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/pathology/reports/cytology')}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                🔙 Quay lại
              </button>
              <button
                onClick={() => setSchema(DEFAULT_CYTOLOGY_SCHEMA)}
                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
              >
                🔄 Reset mặc định
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? '⏳ Đang lưu...' : '💾 Lưu cấu hình'}
              </button>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-bold text-blue-800 mb-2">📋 Hướng dẫn:</h3>
          <ul className="text-sm text-blue-700 space-y-1 ml-4 list-disc">
            <li>Tick ✅ để bật/tắt các sections và fields</li>
            <li>Dùng nút ⬆️⬇️ để sắp xếp thứ tự sections</li>
            <li>Fields bị tắt sẽ không hiển thị trong form tạo phiếu</li>
            <li>Nhấn "Lưu cấu hình" để áp dụng thay đổi cho toàn hệ thống</li>
          </ul>
        </div>

        {/* Form Schema Editor */}
        <div className="space-y-4">
          {schema.sections.map((section, index) => (
            <div
              key={section.id}
              className={`bg-white rounded-lg shadow-md border-l-4 ${
                section.enabled ? 'border-green-500' : 'border-gray-300'
              }`}
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={section.enabled}
                      onChange={() => toggleSection(section.id)}
                      className="w-5 h-5"
                    />
                    <span className={`text-lg font-bold ${section.enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                      {section.order}. {section.title}
                    </span>
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => moveSectionUp(section.id)}
                      disabled={index === 0}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-30"
                    >
                      ⬆️
                    </button>
                    <button
                      onClick={() => moveSectionDown(section.id)}
                      disabled={index === schema.sections.length - 1}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-30"
                    >
                      ⬇️
                    </button>
                    <button
                      onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      {expandedSection === section.id ? '▲ Thu gọn' : '▼ Mở rộng'}
                    </button>
                  </div>
                </div>

                {expandedSection === section.id && section.enabled && (
                  <div className="mt-4 pt-4 border-t space-y-2">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-sm text-gray-600 font-medium">
                        Các trường trong section này:
                      </p>
                      <button
                        onClick={() => openAddFieldModal(section.id)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        ➕ Thêm Field Mới
                      </button>
                    </div>
                    {section.fields.map(field => renderFieldToggle(section, field))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="font-bold text-gray-800 mb-3">📊 Tổng quan:</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded">
              <div className="text-2xl font-bold text-green-600">
                {schema.sections.filter(s => s.enabled).length}
              </div>
              <div className="text-sm text-gray-600">Sections đang bật</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded">
              <div className="text-2xl font-bold text-blue-600">
                {schema.sections.reduce((count, s) => count + s.fields.filter(f => f.enabled).length, 0)}
              </div>
              <div className="text-sm text-gray-600">Fields đang bật</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded">
              <div className="text-2xl font-bold text-gray-600">
                {schema.sections.length}
              </div>
              <div className="text-sm text-gray-600">Tổng số sections</div>
            </div>
          </div>
        </div>

        {/* Field Editor Modal */}
        {fieldEditor.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">
                  {fieldEditor.mode === 'add' ? '➕ Thêm Field Mới' : '✏️ Chỉnh sửa Field'}
                </h2>

                <div className="space-y-4">
                  {/* Field ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID Field <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newField.id || ''}
                      onChange={(e) => setNewField({ ...newField, id: e.target.value })}
                      placeholder="e.g. customField1"
                      className="w-full px-3 py-2 border rounded"
                      disabled={fieldEditor.mode === 'edit'}
                    />
                    <p className="text-xs text-gray-500 mt-1">ID không được trùng với field khác</p>
                  </div>

                  {/* Field Label */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tên hiển thị <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newField.label || ''}
                      onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      placeholder="e.g. Tên trường tùy chỉnh"
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>

                  {/* Field Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Loại Field
                    </label>
                    <select
                      value={newField.type || 'text'}
                      onChange={(e) => setNewField({ ...newField, type: e.target.value as any })}
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="text">Text (Văn bản ngắn)</option>
                      <option value="textarea">Textarea (Văn bản dài)</option>
                      <option value="number">Number (Số)</option>
                      <option value="select">Select (Dropdown)</option>
                      <option value="checkbox">Checkbox (Đánh dấu)</option>
                      <option value="date">Date (Ngày tháng)</option>
                    </select>
                  </div>

                  {/* Options for Select */}
                  {newField.type === 'select' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Options (JSON format)
                      </label>
                      <textarea
                        value={JSON.stringify(newField.options || [], null, 2)}
                        onChange={(e) => {
                          try {
                            const options = JSON.parse(e.target.value)
                            setNewField({ ...newField, options })
                          } catch (err) {
                            // Invalid JSON, ignore
                          }
                        }}
                        placeholder='[{"value":"option1","label":"Option 1"}]'
                        className="w-full px-3 py-2 border rounded font-mono text-sm"
                        rows={4}
                      />
                    </div>
                  )}

                  {/* Placeholder */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placeholder (Gợi ý nhập liệu)
                    </label>
                    <input
                      type="text"
                      value={newField.placeholder || ''}
                      onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                      placeholder="e.g. Nhập thông tin..."
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>

                  {/* Required */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={newField.required || false}
                      onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">
                      Trường bắt buộc (Required)
                    </label>
                  </div>

                  {/* Enabled */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={newField.enabled !== false}
                      onChange={(e) => setNewField({ ...newField, enabled: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">
                      Bật field này (Enabled)
                    </label>
                  </div>

                  {/* Order */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Thứ tự hiển thị
                    </label>
                    <input
                      type="number"
                      value={newField.order || 1}
                      onChange={(e) => setNewField({ ...newField, order: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded"
                      min="1"
                    />
                  </div>

                  {/* Layout Options */}
                  <FieldLayoutEditor
                    field={newField}
                    onChange={(updates) => setNewField(prev => ({ ...prev, ...updates } as any))}
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                  <button
                    onClick={closeFieldModal}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={saveField}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {fieldEditor.mode === 'add' ? 'Thêm Field' : 'Lưu thay đổi'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CytologyFormBuilder
